import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  FileText,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { mockApplications, getApplicationSteps } from '@mocks/licenses';
import { mockApplicants } from '@mocks/applicants';
import { mockAttachments } from '@mocks/attachments';
import { LICENSE_CATEGORY_LABELS } from '@utils/constants';

export const EnrolmentReview: React.FC = () => {
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [applications, setApplications] = useState(mockApplications);

  // Get pending/under review applications
  const pendingApplications = applications.filter(
    (app) => app.status !== 'APPROVED' && app.status !== 'REJECTED'
  );

  const selectedApp = selectedAppId
    ? applications.find((app) => app.id === selectedAppId)
    : null;

  const selectedApplicant = selectedApp
    ? mockApplicants.find((a) => a.id === selectedApp.applicant_id)
    : null;

  const applicantDocuments = selectedApplicant
    ? mockAttachments.filter((att) => att.applicant_id === selectedApplicant.id)
    : [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleApprove = async () => {
    if (!selectedApp) return;

    setIsProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log('✅ Mock: Application approved', {
      application_id: selectedApp.id,
      notes: reviewNotes,
    });

    setApplications(
      applications.map((app) =>
        app.id === selectedApp.id ? { ...app, status: 'APPROVED' as const } : app
      )
    );

    toast.success(`Application #${selectedApp.id} approved successfully!`);
    setIsProcessing(false);
    setSelectedAppId(null);
    setReviewNotes('');
  };

  const handleReject = async () => {
    if (!selectedApp || !reviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    console.log('✅ Mock: Application rejected', {
      application_id: selectedApp.id,
      reason: reviewNotes,
    });

    setApplications(
      applications.map((app) =>
        app.id === selectedApp.id
          ? { ...app, status: 'REJECTED' as const, rejection_reason: reviewNotes }
          : app
      )
    );

    toast.success(`Application #${selectedApp.id} rejected`);
    setIsProcessing(false);
    setSelectedAppId(null);
    setReviewNotes('');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Approved</Badge>;
      case 'REJECTED':
        return <Badge variant="danger" icon={<XCircle className="h-3 w-3" />}>Rejected</Badge>;
      case 'DOCUMENT_REVIEW':
        return <Badge variant="warning" icon={<Clock className="h-3 w-3" />}>Document Review</Badge>;
      default:
        return <Badge variant="default">{status.replace('_', ' ')}</Badge>;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-ncaa-dark">Enrolment Review</h1>
        <p className="text-ncaa-medium mt-1">
          Review and approve pending license applications
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Application List - Left Side */}
        <div className="lg:col-span-1">
          <Card title="Pending Applications" icon={<FileText className="h-5 w-5" />}>
            <div className="space-y-2">
              {pendingApplications.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No pending applications</p>
                </div>
              ) : (
                pendingApplications.map((app) => {
                  const applicant = mockApplicants.find((a) => a.id === app.applicant_id);
                  const isSelected = selectedAppId === app.id;

                  return (
                    <button
                      key={app.id}
                      onClick={() => setSelectedAppId(app.id)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-ncaa-primary bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {applicant?.first_name} {applicant?.last_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge 
                            variant={app.application_type === 'NEW' ? 'info' : 'warning'} 
                            size="sm"
                          >
                            {app.application_type === 'NEW' ? 'New' : 'Renewal'}
                          </Badge>
                          <p className="text-xs text-gray-600">
                            {LICENSE_CATEGORY_LABELS[app.license_category]}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(app.status)}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                        <Calendar className="h-3 w-3" />
                        {formatDate(app.submitted_at || app.created_at)}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </Card>
        </div>

        {/* Application Details - Right Side */}
        <div className="lg:col-span-2">
          {!selectedApp ? (
            <Card>
              <div className="text-center py-16">
                <Eye className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Select an Application
                </h3>
                <p className="text-gray-600">Choose an application from the list to review</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Applicant Info */}
              <Card title="Applicant Information" icon={<User className="h-5 w-5" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">
                      {selectedApplicant?.first_name} {selectedApplicant?.middle_name}{' '}
                      {selectedApplicant?.last_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">NIN</p>
                    <p className="font-medium text-gray-900">{selectedApplicant?.nin}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{selectedApplicant?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">{selectedApplicant?.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Date of Birth</p>
                    <p className="font-medium text-gray-900">
                      {selectedApplicant && formatDate(selectedApplicant.dob)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Nationality</p>
                    <p className="font-medium text-gray-900">{selectedApplicant?.nationality}</p>
                  </div>
                </div>
              </Card>

              {/* Application Details */}
              <Card title="Application Details" icon={<FileText className="h-5 w-5" />}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Application Type</p>
                      <div className="flex items-center gap-2">
                        <Badge variant={selectedApp.application_type === 'NEW' ? 'info' : 'warning'}>
                          {selectedApp.application_type === 'NEW' ? 'New License' : 'License Renewal'}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">License Category</p>
                      <p className="font-semibold text-gray-900">
                        {LICENSE_CATEGORY_LABELS[selectedApp.license_category]}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      {getStatusBadge(selectedApp.status)}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Submitted</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(selectedApp.submitted_at || selectedApp.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Progress</p>
                      <p className="font-medium text-gray-900">
                        Step {selectedApp.current_step} of {selectedApp.total_steps}
                      </p>
                    </div>
                  </div>

                  {selectedApp.application_type === 'RENEWAL' && selectedApp.existing_license_number && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Existing License Number (Renewal)</p>
                        <p className="text-sm font-mono font-semibold text-gray-900">
                          {selectedApp.existing_license_number}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedApp.form_data && Object.keys(selectedApp.form_data).length > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">Application Data</p>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {Object.entries(selectedApp.form_data).map(([key, value]) => (
                          <div key={key} className="p-2 bg-gray-50 rounded">
                            <p className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className="font-medium text-gray-900">{String(value)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Documents */}
              <Card title="Uploaded Documents" icon={<FileText className="h-5 w-5" />}>
                <div className="space-y-3">
                  {applicantDocuments.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">No documents uploaded</p>
                  ) : (
                    applicantDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.document_type}</p>
                            <p className="text-xs text-gray-600">{doc.file_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={doc.status === 'VERIFIED' ? 'success' : 'warning'}>
                            {doc.status}
                          </Badge>
                          <Button size="sm" variant="outline" icon={<Eye className="h-3 w-3" />}>
                            View
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              {/* Review Section */}
              <Card title="Review Decision" icon={<MessageSquare className="h-5 w-5" />}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review Notes {selectedApp.status !== 'APPROVED' && '*'}
                    </label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      rows={4}
                      placeholder="Add notes about your decision..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ncaa-primary focus:border-transparent"
                    />
                  </div>

                  {selectedApp.reviewer_notes && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-1">Previous Notes</p>
                      <p className="text-sm text-blue-800">{selectedApp.reviewer_notes}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <Button
                      onClick={handleApprove}
                      loading={isProcessing}
                      icon={<CheckCircle className="h-4 w-4" />}
                      className="flex-1"
                    >
                      Approve Application
                    </Button>
                    <Button
                      onClick={handleReject}
                      loading={isProcessing}
                      variant="outline"
                      icon={<XCircle className="h-4 w-4" />}
                      className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Reject Application
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
